import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DataGrid } from '@mui/x-data-grid';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useSnackbar } from 'notistack';
import DashboardLayout from '../layouts/DashboardLayout';
import analyticsApi from '../api/analytics';
import StatCard from '../components/StatCard';
import { formatDate } from '../utils/format';

const AnalyticsPage = () => {
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [educators, setEducators] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [atRisk, setAtRisk] = useState([]);
  const [attendanceByClass, setAttendanceByClass] = useState([]);
  const [attendanceBySubject, setAttendanceBySubject] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [termParams, setTermParams] = useState({ studentId: '', term: 'Term 1', year: new Date().getFullYear() });
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [summaryData, monthlyData, educatorData, engagementData, atRiskData, classData, subjectData, heatmapData] = await Promise.all([
          analyticsApi.getSummary(),
          analyticsApi.getMonthlyActivity(),
          analyticsApi.getEducatorBreakdown(),
          analyticsApi.getEngagement(),
          analyticsApi.getAtRisk(),
          analyticsApi.getAttendanceByClass(),
          analyticsApi.getAttendanceBySubject(),
          analyticsApi.getSubmissionHeatmap(),
        ]);
        setSummary(summaryData);
        setMonthly(monthlyData);
        setEducators(educatorData);
        setEngagement(engagementData);
 setAtRisk(Array.isArray(atRiskData) ? atRiskData : []);
 setAttendanceByClass(Array.isArray(classData) ? classData : []);
 setAttendanceBySubject(Array.isArray(subjectData) ? subjectData : []);
        setHeatmap(heatmapData || null);
      } catch (err) {
        enqueueSnackbar(err?.response?.data?.message || 'Failed to load analytics', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [enqueueSnackbar]);

  const columns = [
    {
      field: 'educator',
      headerName: 'Educator',
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => `${params.row.educator?.name || 'Unknown'} ${params.row.educator?.surname || ''}`,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 240,
      valueGetter: (params) => params.row.educator?.email || '',
    },
    {
      field: 'sessionCount',
      headerName: 'Sessions Hosted',
      minWidth: 180,
    },
  ];

  const handleExportCsv = () => {
    const header = ['Educator Name', 'Email', 'Sessions Hosted'];
    const rows = educators.map((entry) => [
      `${entry.educator?.name || 'Unknown'} ${entry.educator?.surname || ''}`.trim(),
      entry.educator?.email || '',
      entry.sessionCount,
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `educator-analytics-${formatDate(new Date())}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const heatmapRows = useMemo(() => {
    if (!heatmap?.days?.length || !heatmap?.grades?.length) return [];
    return heatmap.grades.map((grade) => {
      const row = { id: `g-${grade}`, grade: `Grade ${grade}` };
      heatmap.days.forEach((day) => {
        row[day] = heatmap.matrix?.[grade]?.[day] ?? 0;
      });
      return row;
    });
  }, [heatmap]);

  const heatmapColumns = useMemo(() => {
    if (!heatmap?.days?.length) return [];
    return [
      { field: 'grade', headerName: 'Grade', minWidth: 120 },
      ...heatmap.days.map((day) => ({
        field: day,
        headerName: day,
        minWidth: 110,
      })),
    ];
  }, [heatmap]);

  return (
    <DashboardLayout
      title="Analytics"
      actions={(
        <Stack direction="row" spacing={1}>
          <Button startIcon={<FileDownloadIcon />} variant="outlined" onClick={handleExportCsv} disabled={!educators.length}>
            Export educators CSV
          </Button>
          <Button startIcon={<FileDownloadIcon />} variant="outlined" href="/api/exports/attendance.csv">
            Attendance CSV
          </Button>
          <Button startIcon={<FileDownloadIcon />} variant="outlined" href="/api/exports/submissions.csv">
            Submissions CSV
          </Button>
          <Button startIcon={<FileDownloadIcon />} variant="outlined" href="/api/exports/attendance-by-subject.csv">
            Attendance by subject CSV
          </Button>
        </Stack>
      )}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <StatCard label="Students" value={summary?.studentCount ?? 0} />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard label="Educators" value={summary?.educatorCount ?? 0} />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard label="Total Sessions" value={summary?.sessionCount ?? 0} />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard label="Upcoming Sessions" value={summary?.upcomingCount ?? 0} />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard label="Attendance avg" value={engagement?.attendanceAvg != null ? `${engagement.attendanceAvg}%` : '...'} />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard label="Submission avg" value={engagement?.submissionAvg != null ? `${engagement.submissionAvg}%` : '...'} />
        </Grid>

        <Grid item xs={12} md={7}>
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Monthly Session Volume
                </Typography>
                <Box sx={{ height: 360 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthly}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1a237e" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Top Educator Activity
                </Typography>
                <Box sx={{ height: 360 }}>
                  <DataGrid
                    rows={educators.map((entry, index) => ({ id: index, ...entry }))}
                    columns={columns}
                    loading={loading}
                    hideFooterSelectedRowCount
                    disableRowSelectionOnClick
                    pageSizeOptions={[5]}
                    initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  At-risk learners (attendance {'<'}70% or submissions {'<'}50%)
                </Typography>
                {atRisk.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    None flagged yet.
                  </Typography>
                ) : (
                  <Box sx={{ height: 320 }}>
                    <DataGrid
                      rows={atRisk.map((row, idx) => ({
                        id: idx,
                        name: `${row.student?.name || ''} ${row.student?.surname || ''}`.trim(),
                        grade: row.grade,
                        attendanceAvg: row.attendanceAvg,
                        submissionRate: row.submissionRate,
                      }))}
                      columns={[
                        { field: 'name', headerName: 'Learner', flex: 1 },
                        { field: 'grade', headerName: 'Grade', width: 120 },
                        { field: 'attendanceAvg', headerName: 'Attendance %', width: 150 },
                        { field: 'submissionRate', headerName: 'Submission %', width: 150 },
                      ]}
                      hideFooterSelectedRowCount
                      density="compact"
                      pageSizeOptions={[5]}
                      initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                    />
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Attendance by class
                </Typography>
                <Box sx={{ height: 360 }}>
                  <DataGrid
                    rows={attendanceByClass.map((row, idx) => ({
                      id: row.sessionId || idx,
                      title: row.title,
                      subject: row.subject,
                      grade: row.grade,
 educator: row.educator ? `${row.educator.name || ''} ${row.educator.surname || ''}`.trim() : '',
                      section: row.section?.section || '',
                      startTime: row.startTime,
                      attendanceRate: row.attendanceRate,
                      present: row.present,
                      absent: row.absent,
                    }))}
                    columns={[
                      { field: 'title', headerName: 'Session', flex: 1, minWidth: 200 },
                      { field: 'subject', headerName: 'Subject', minWidth: 160 },
                      { field: 'grade', headerName: 'Grade', minWidth: 100 },
                      { field: 'educator', headerName: 'Educator', minWidth: 200 },
                      { field: 'section', headerName: 'Section', minWidth: 120 },
                      { field: 'attendanceRate', headerName: 'Attendance %', minWidth: 140 },
                      { field: 'present', headerName: 'Present', minWidth: 110 },
                      { field: 'absent', headerName: 'Absent', minWidth: 110 },
                    ]}
                    loading={loading}
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    density="compact"
                    pageSizeOptions={[10]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Attendance by subject
                </Typography>
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={attendanceBySubject}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="subject" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="attendanceRate" fill="#0f766e" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Submission heatmap
                </Typography>
                <Box sx={{ height: 320 }}>
                  <DataGrid
                    rows={heatmapRows}
                    columns={heatmapColumns}
                    loading={loading}
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    density="compact"
                    pageSizeOptions={[5]}
                    initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Term reports (PDF)
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <TextField
                    label="Student ID"
                    value={termParams.studentId}
                    onChange={(e) => setTermParams((prev) => ({ ...prev, studentId: e.target.value }))}
                    sx={{ minWidth: 240 }}
                  />
                  <TextField
                    select
                    label="Term"
                    value={termParams.term}
                    onChange={(e) => setTermParams((prev) => ({ ...prev, term: e.target.value }))}
                    sx={{ minWidth: 140 }}
                  >
                    {['Term 1', 'Term 2', 'Term 3', 'Term 4'].map((term) => (
                      <MenuItem key={term} value={term}>
                        {term}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Year"
                    type="number"
                    value={termParams.year}
                    onChange={(e) => setTermParams((prev) => ({ ...prev, year: e.target.value }))}
                    sx={{ minWidth: 120 }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    disabled={!termParams.studentId}
                    onClick={async () => {
                      const blob = await analyticsApi.getTermReport({
                        studentId: termParams.studentId,
                        term: termParams.term,
                        year: termParams.year,
                      });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `term-report-${termParams.year}-${termParams.term}-${termParams.studentId}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Download PDF
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
